import { useCallback, useState } from 'react';
import { ScrollView, Text, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useAuth } from '../context/AuthContext';
import { getUserDetail, suspendUser, unsuspendUser, anonymizeUser, AdminUserDetail } from '../api/admin';
import { Button, Card, ConfirmDialog, CoinIcon, ErrorText, FormInput, Label, ScreenContainer } from '../components/ui';
import { colors, fonts } from '../theme';
import { ApiError } from '../api/client';

function Row({ label, value }: { label: string; value: string | number | boolean | null | undefined }) {
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4 }}>
      <Text style={{ color: colors.textMuted, fontSize: 13 }}>{label}</Text>
      <Text style={{ color: colors.text, fontSize: 13, fontFamily: fonts.semiBold }}>
        {value === null || value === undefined || value === '' ? '—' : String(value)}
      </Text>
    </View>
  );
}

export default function AdminUserDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { token } = useAuth();
  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmSuspendVisible, setConfirmSuspendVisible] = useState(false);
  const [confirmAnonymizeVisible, setConfirmAnonymizeVisible] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      setDetail(await getUserDetail(token, id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Tải hồ sơ thất bại');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onSuspend = () => {
    if (!reason.trim()) {
      setError('Vui lòng nhập lý do khóa tài khoản');
      return;
    }
    setConfirmSuspendVisible(true);
  };

  const confirmSuspend = async () => {
    if (!token || !id) return;
    setBusy(true);
    setError(null);
    try {
      setDetail(await suspendUser(token, id, reason));
      setReason('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Khóa tài khoản thất bại');
    } finally {
      setBusy(false);
      setConfirmSuspendVisible(false);
    }
  };

  const onUnsuspend = async () => {
    if (!token || !id) return;
    setBusy(true);
    setError(null);
    try {
      setDetail(await unsuspendUser(token, id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Mở khóa thất bại');
    } finally {
      setBusy(false);
    }
  };

  const onAnonymize = () => {
    setConfirmAnonymizeVisible(true);
  };

  const confirmAnonymize = async () => {
    if (!token || !id) return;
    setBusy(true);
    setError(null);
    try {
      await anonymizeUser(token, id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Xoá dữ liệu thất bại');
    } finally {
      setBusy(false);
      setConfirmAnonymizeVisible(false);
    }
  };

  if (loading && !detail) {
    return (
      <ScreenContainer>
        <Text style={{ color: colors.textMuted }}>Đang tải...</Text>
      </ScreenContainer>
    );
  }
  if (!detail) {
    return (
      <ScreenContainer>
        <ErrorText>{error}</ErrorText>
      </ScreenContainer>
    );
  }

  return (
    <ScreenContainer>
      <ScrollView showsVerticalScrollIndicator={false}>
        <Card>
          <Text style={{ fontFamily: fonts.bold, fontSize: 18, color: colors.text }}>
            {detail.name} {detail.suspendedAt ? '🚫 Đã khóa' : ''}
          </Text>
          <Text style={{ color: colors.textMuted, marginBottom: 8 }}>{detail.email}</Text>
          <Row label="Vai trò đăng ký" value={detail.role} />
          <Row label="Có vai trò người nhận việc" value={detail.hasReviewerRole} />
          <Row label="Có vai trò người đăng việc" value={detail.hasOwnerRole} />
          <Row label="Loại tài khoản đăng việc" value={detail.posterKind} />
          <Row label="Ngày tạo" value={new Date(detail.createdAt).toLocaleString('vi-VN')} />
        </Card>

        <Card>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>Ví & xác minh</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6 }}>
            <Text style={{ fontFamily: fonts.bold, fontSize: 18, color: colors.primary }}>{detail.coinBalance}</Text>
            <CoinIcon size={16} />
          </View>
          <Row label="Tổng đã nạp (VND)" value={detail.cumulativeTopupVnd.toLocaleString('vi-VN')} />
          <Row label="Đã xác minh CCCD" value={detail.identityVerified} />
          <Row label="Trạng thái xác minh CCCD" value={detail.identityVerificationStatus} />
          <Row label="Số điện thoại" value={detail.phone} />
          <Row label="SĐT đã xác minh" value={detail.phoneVerified} />
          <Row label="Email đã xác minh" value={detail.emailVerified} />
          <Row label="Mã giới thiệu" value={detail.referralCode} />
        </Card>

        {detail.hasOwnerRole ? (
          <Card>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>Người đăng việc</Text>
            <Row label="Hạng" value={detail.ownerTier} />
            <Row label="Phí dịch vụ" value={detail.ownerEscrowFeePercent != null ? `${detail.ownerEscrowFeePercent}%` : undefined} />
            <Row label="VIP đang hoạt động" value={detail.vipActive} />
            <Row label="Số shop" value={detail.shopCount} />
            <Row label="Số nhiệm vụ đã tạo" value={detail.taskCount} />
          </Card>
        ) : null}

        {detail.hasReviewerRole ? (
          <Card>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>Người nhận việc</Text>
            <Row label="Hạng uy tín" value={detail.reviewerTier} />
            <Row label="Phí rút" value={detail.withdrawalFeePercent != null ? `${detail.withdrawalFeePercent}%` : undefined} />
            <Row label="Đã hoàn thành" value={detail.completedTaskCount} />
            <Row label="Đã bị từ chối" value={detail.rejectedTaskCount} />
            <Row label="Tổng số bài nộp" value={detail.submissionCount} />
          </Card>
        ) : null}

        <Card>
          <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>Quản trị</Text>
          <ErrorText>{error}</ErrorText>
          {detail.suspendedAt ? (
            <>
              <Row label="Lý do khóa" value={detail.suspendedReason} />
              <Row label="Khóa lúc" value={new Date(detail.suspendedAt).toLocaleString('vi-VN')} />
              <Button title="Mở khóa tài khoản" onPress={onUnsuspend} loading={busy} />
            </>
          ) : detail.role === 'ADMIN' ? (
            <Text style={{ color: colors.textMuted, fontSize: 13 }}>Không thể khóa tài khoản quản trị viên</Text>
          ) : (
            <>
              <Label>Lý do khóa tài khoản</Label>
              <FormInput value={reason} onChangeText={setReason} placeholder="Ví dụ: gian lận, spam, vi phạm điều khoản" />
              <Button title="Khóa tài khoản" variant="danger" onPress={onSuspend} loading={busy} />
            </>
          )}
        </Card>

        {detail.role !== 'ADMIN' ? (
          <Card>
            <Text style={{ fontFamily: fonts.bold, color: colors.text, marginBottom: 6 }}>
              Xoá dữ liệu theo yêu cầu người dùng
            </Text>
            <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 10 }}>
              Dùng khi người dùng gửi yêu cầu xoá tài khoản/dữ liệu qua email (theo Chính sách quyền
              riêng tư). Xoá vĩnh viễn, không thể hoàn tác.
            </Text>
            <Button title="Xoá dữ liệu tài khoản" variant="danger" onPress={onAnonymize} loading={busy} />
          </Card>
        ) : null}
      </ScrollView>
      <ConfirmDialog
        visible={confirmSuspendVisible}
        title="Xác nhận khóa tài khoản"
        message={`Khóa tài khoản "${detail?.name}"? Họ sẽ không thể đăng nhập được nữa.`}
        confirmLabel="Khóa"
        danger
        loading={busy}
        onConfirm={confirmSuspend}
        onCancel={() => setConfirmSuspendVisible(false)}
      />
      <ConfirmDialog
        visible={confirmAnonymizeVisible}
        title="Xoá dữ liệu tài khoản"
        message={`Xoá vĩnh viễn email, tên, SĐT, CCCD, ảnh CCCD và thông tin ngân hàng của "${detail?.name}"? Lịch sử giao dịch/nhiệm vụ vẫn được giữ lại để đối soát, nhưng tài khoản sẽ bị khoá vĩnh viễn. Không thể hoàn tác.`}
        confirmLabel="Xoá dữ liệu"
        danger
        loading={busy}
        onConfirm={confirmAnonymize}
        onCancel={() => setConfirmAnonymizeVisible(false)}
      />
    </ScreenContainer>
  );
}
